import React, { useMemo, useState } from 'react';
import { Modal, Button, Select } from 'antd';
import { SyncOutlined, TeamOutlined, WarningFilled } from '@ant-design/icons';
import { UserDisplay } from '../../../../pages/users/types/UserDisplay';
import { ContactsTransferAnswer, ContactsTransferQuestion, getContactsTransferChoices } from '../../../../hooks/subscription/useNumberAssignmentSteps';
import ContactsTransferText from '../../../@shared/number-assignment/ContactsTransferText';
import NumberAssignmentProgress from '../../../@shared/number-assignment/NumberAssignmentProgress';
import './ChangeNumberModal.css';

interface ChangeNumberModalProps {
  isOpen: boolean;
  user: UserDisplay | null;
  isLoading?: boolean;
  /** All company numbers (free + assigned), with user info in label */
  allNumbers?: { value: string; label: string }[];
  /** Asked once confirmed, when other users hold contacts on the new number */
  transferQuestion?: ContactsTransferQuestion | null;
  /** The backend is moving the number and its contacts */
  isAssigning?: boolean;
  onCancel: () => void;
  onConfirm: (newSubscriptionId: string) => void;
  onAnswerTransfer: (transfer: ContactsTransferAnswer) => void;
}

const ChangeNumberModal: React.FC<ChangeNumberModalProps> = ({
  isOpen,
  user,
  isLoading = false,
  allNumbers = [],
  transferQuestion = null,
  isAssigning = false,
  onCancel,
  onConfirm,
  onAnswerTransfer,
}) => {
  const [selectedNumber, setSelectedNumber] = useState<string | undefined>(undefined);

  // Reset selection whenever the modal opens or closes
  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setSelectedNumber(undefined);
  }

  // Exclude the user's current subscription from the list
  const filteredNumbers = useMemo(() => {
    if (!user?.idSubscription) return allNumbers;
    return allNumbers.filter(n => n.value !== user.idSubscription);
  }, [allNumbers, user?.idSubscription]);

  const hasCurrentNumber = user?.sender && user.sender.length > 0;
  const title = hasCurrentNumber ? 'Changer de numéro' : 'Assigner un numéro';
  const confirmLabel = hasCurrentNumber ? 'Changer de numéro' : 'Assigner le numéro';
  // Once the question is answered, the modal stays open until the number moved. Before, closing it cancels: nothing is written
  const isLocked = isAssigning;
  const transferChoices = transferQuestion ? getContactsTransferChoices(transferQuestion) : null;

  if (!user) return null;

  const handleConfirm = () => {
    if (selectedNumber) {
      onConfirm(selectedNumber);
    }
  };

  const handleCancel = () => {
    setSelectedNumber(undefined);
    onCancel();
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      centered
      width={612}
      className="change-number-modal"
      closable={!isLocked}
      maskClosable={!isLocked}
      keyboard={!isLocked}
    >
      <div className="change-number-modal__content">
        <div className="change-number-modal__header">
          <SyncOutlined className="change-number-modal__icon" />
          <h3 className="change-number-modal__title">{title}</h3>
        </div>

        {isAssigning ? (
          <NumberAssignmentProgress />
        ) : transferQuestion && transferChoices ? (
          <>
            <div className="change-number-modal__warning-box">
              <div className="change-number-modal__warning-header">
                <TeamOutlined className="change-number-modal__warning-icon" />
                <span className="change-number-modal__warning-title">Contacts déjà sur ce numéro</span>
              </div>
              <p className="change-number-modal__warning-text">
                <ContactsTransferText question={transferQuestion} />
              </p>
            </div>

            <div className="change-number-modal__actions">
              <Button className="change-number-modal__cancel-btn" onClick={() => onAnswerTransfer(transferChoices.secondary.answer)}>
                {transferChoices.secondary.label}
              </Button>
              <Button type="primary" className="change-number-modal__confirm-btn" onClick={() => onAnswerTransfer(transferChoices.primary.answer)}>
                {transferChoices.primary.label}
              </Button>
            </div>
          </>
        ) : (
          <>
            {hasCurrentNumber && (
              <div className="change-number-modal__warning-box">
                <div className="change-number-modal__warning-header">
                  <WarningFilled className="change-number-modal__warning-icon" />
                  <span className="change-number-modal__warning-title">Numéro actuel</span>
                </div>
                <p className="change-number-modal__warning-text">
                  {user.name} utilise actuellement le <strong>{user.sender.join(', ')}</strong>. Ce numéro sera
                  libéré et passera en statut "Disponible".
                </p>
              </div>
            )}

            <div className="change-number-modal__select-section">
              <label className="change-number-modal__label">
                {hasCurrentNumber ? 'Nouveau numéro' : 'Numéro à assigner'}
              </label>
              <Select
                placeholder="Sélectionner un numéro"
                value={selectedNumber}
                onChange={setSelectedNumber}
                options={filteredNumbers}
                className="change-number-modal__select"
              />
            </div>

            <div className="change-number-modal__actions">
              <Button className="change-number-modal__cancel-btn" onClick={handleCancel}>
                Annuler
              </Button>
              <Button
                type="primary"
                className="change-number-modal__confirm-btn"
                onClick={handleConfirm}
                loading={isLoading}
                disabled={!selectedNumber}
              >
                {confirmLabel}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ChangeNumberModal;
